import { motion } from "framer-motion";
import {
  FaTimes,
  FaSpinner,
  FaPlane,
  FaCheckCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaSuitcase,
  FaExchangeAlt,
} from "react-icons/fa";
import { useCurrency } from "../../context/CurrencyContext";
import { formatPriceSync } from "../../utils/currency";

const PriceModal = ({
  showPriceModal,
  setShowPriceModal,
  selectedFlightForPrice,
  isLoadingPrices,
  priceOptions,
  handleAuthCheck,
  flightType = "onward", // 'onward' or 'return'
}) => {
  if (!showPriceModal) return null;

  return (
    <div className="fixed inset-0 bg-black/60 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <ModalHeader
          setShowPriceModal={setShowPriceModal}
          selectedFlightForPrice={selectedFlightForPrice}
          flightType={flightType}
        />
        <ModalContent
          isLoadingPrices={isLoadingPrices}
          priceOptions={priceOptions}
          handleAuthCheck={handleAuthCheck}
          flightType={flightType}
        />
      </motion.div>
    </div>
  );
};

const ModalHeader = ({
  setShowPriceModal,
  selectedFlightForPrice,
  flightType,
}) => (
  <div className="flex items-center justify-between p-6 border-b border-gray-200">
    <div>
      <h2 className="text-2xl font-bold text-gray-900 capitalize">
        {flightType} Flight - Choose your fare type
      </h2>
      <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <FaPlane className="text-blue-500" />
          <span>
            {selectedFlightForPrice?.Flights?.AirlineName || "Flight"}
          </span>
        </div>
        <span>•</span>
        <span>
          {selectedFlightForPrice?.Flights?.Onward?.[0]?.depCode} →
          {
            selectedFlightForPrice?.Flights?.Onward?.[
              selectedFlightForPrice?.Flights?.Onward?.length - 1
            ]?.arrCode
          }
        </span>
        <span>•</span>
        <span>{selectedFlightForPrice?.Flights?.Onward?.[0]?.flightNo}</span>
      </div>
    </div>
    <button
      onClick={() => setShowPriceModal(false)}
      className="text-gray-500 hover:text-gray-700 text-2xl"
    >
      <FaTimes />
    </button>
  </div>
);

const ModalContent = ({
  isLoadingPrices,
  priceOptions,
  handleAuthCheck,
  flightType,
}) => (
  <div className="p-6">
    {isLoadingPrices ? (
      <div className="text-center py-12">
        <FaSpinner className="animate-spin text-4xl text-blue-600 mx-auto mb-4" />
        <p className="text-gray-600">Loading fare options...</p>
      </div>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {Array.isArray(priceOptions) && priceOptions.length > 0 ? (
          priceOptions.map((fare, index) => (
            <FareOption
              key={index}
              fare={fare}
              index={index}
              handleAuthCheck={handleAuthCheck}
              flightType={flightType}
            />
          ))
        ) : (
          <NoFareOptions />
        )}
      </div>
    )}
  </div>
);

const FareOption = ({ fare, index, handleAuthCheck, flightType }) => {
  const { currency } = useCurrency();
  const handleBookClick = () => {
    // Mark which flight type this fare belongs to
    handleAuthCheck({
      ...fare,
      flightType: flightType, // 'onward' or 'return'
    });
  };

  return (
    <div
      className={`border-2 rounded-xl p-6 transition-all duration-300 hover:shadow-lg
      ${
        index === 0
          ? "border-blue-500 bg-blue-50/30"
          : index === 1
          ? "border-green-500 bg-green-50/30"
          : "border-purple-500 bg-purple-50/30"
      }`}
    >
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <div
            className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold mb-3
            ${
              index === 0
                ? "bg-blue-100 text-blue-800"
                : index === 1
                ? "bg-green-100 text-green-800"
                : "bg-purple-100 text-purple-800"
            }`}
          >
            <span>★</span>
            <span>{fare.fareName || "Economy"}</span>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-bold text-gray-900">
              {formatPriceSync(fare.price || 9451, currency)}
            </span>
            <span className="text-sm text-gray-500">per adult</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-500 mb-1">Cancellation</div>
          <div
            className={`text-sm font-semibold ${
              fare.refundable?.includes("Refundable")
                ? "text-green-600"
                : "text-orange-600"
            }`}
          >
            {fare.refundable || "Partially Refundable"}
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <FeatureItem
          icon={<FaSuitcase />}
          title="Baggage"
          value={`${fare.baggage?.checkin || "15"} Kgs`}
          color="text-blue-600"
        />
        <FeatureItem
          icon={<FaExchangeAlt />}
          title="Date Change"
          value={`${fare.dateChangeFee || "Contact Airlines"}`}
          color="text-orange-600"
        />
        <FeatureItem
          icon={<FaCheckCircle />}
          title="Meals"
          value={fare.meals === "free" ? "Complimentary" : "Available"}
          color="text-green-600"
        />
        <FeatureItem
          icon={<FaInfoCircle />}
          title="Seats"
          value={fare.seats === "free" ? "Free" : "Chargeable"}
          color="text-purple-600"
        />
      </div>

      {/* Book Button */}
      <button
        onClick={handleBookClick}
        className={`w-full py-3 rounded-lg font-semibold transition-all duration-300
          ${
            index === 0
              ? "bg-blue-600 hover:bg-blue-700 text-white"
              : index === 1
              ? "bg-green-600 hover:bg-green-700 text-white"
              : "bg-purple-600 hover:bg-purple-700 text-white"
          }`}
      >
        SELECT THIS FARE
      </button>

      {/* Footer */}
      <div className="mt-4 text-center">
        <button className="text-sm text-blue-600 hover:text-blue-800 font-medium">
          View Fare Rules →
        </button>
      </div>
    </div>
  );
};

const FeatureItem = ({ icon, title, value, color }) => (
  <div className="flex items-center gap-2">
    <div className={`${color}`}>{icon}</div>
    <div>
      <div className="text-xs text-gray-500">{title}</div>
      <div className="text-sm font-medium text-gray-800">{value}</div>
    </div>
  </div>
);

const NoFareOptions = () => (
  <div className="text-center py-12 col-span-3">
    <FaPlane className="text-4xl text-gray-400 mx-auto mb-4" />
    <h3 className="text-xl font-semibold text-gray-600 mb-2">
      No Fare Options Available
    </h3>
    <p className="text-gray-500">
      No fare options found for this flight. Please try again.
    </p>
  </div>
);

export default PriceModal;
